import Image from 'next/legacy/image'
import { useTranslation } from 'next-i18next'
import styled, { css } from 'styled-components'
import { anyPass, equals } from 'ramda'
import {
  Box,
  Button as DSButton,
  IconArrowUpRight,
  IconLockOutline,
  Inline,
  Skeleton,
  useDialogController
} from '@machobear/design-system'
import { vars } from '@machobear/design-system/css'

import { colors } from '@/styles'
import { isEmptyOrNull } from '@/utils'
import { blurImage } from '@/utils/image'
import { getUrl } from '@/utils/listings'
import { useEnsureLoggedIn } from '@/hooks/use-ensure-loggedIn'
import DSLoginModal from '@/features/Auth/login-modal/ds-login-modal'
import BlurredText from '@/components/blurred-text'
import { ListingCardActions } from '@/components/listing-card/actions'
import { setListingCardDataLayer } from '@/components/listing-card/utils'
import { PillWrapper, VerifiedPill } from '@/components/pill'
import { useTermsDialogContext } from '@/components/terms-dialog/context'

const Container = styled.div``

const Wrapper = styled.div`
  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0);
    transition: background-color 0.2s ease-in-out;
    border-radius: 12px;
  }

  width: 100%;
  border-radius: 12px;
  position: relative;
  height: ${props => props.height}px;
  background: ${vars.backgroundColor.neutralLight} center no-repeat;
  background-size: cover;
  display: flex;
  align-items: flex-end;
  animation: fadeIn ease-in-out 0.2s;
  -webkit-mask-image: -webkit-radial-gradient(white, black);

  @keyframes fadeIn {
    0% {
      opacity: 0.2;
    }
    100% {
      opacity: 1;
    }
  }
`

const DetailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`

const PriceRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 4px;
`

const Amount = styled.span`
  font-size: 20px;
  line-height: 28px;
  font-weight: 700;
  color: ${vars.foregroundColor.neutral};
`

const priceStyles = css`
  color: ${colors.white};
`

const isTrue = anyPass([equals('1'), equals(1), equals(true)])

const defaultInfoHeight = 206

const SingleUnitBlurred = ({
  listing = {},
  height = 315,
  infoHeight = defaultInfoHeight,
  newTab = false,
  noButtons = false,
  skeleton = false,
  isRow = false,
  agreedToTerms = false,
  isLoggedIn
}) => {
  const { t } = useTranslation()
  const { showModalForRequiredTerms } = useTermsDialogContext()

  const isRented =
    listing.state_machine === 'rented' || isTrue(listing.marked_as_rented)

  const size = {
    image: height,
    text: isRented || noButtons ? infoHeight - 60 : infoHeight
  }

  const unitImageFiles = listing.unit_files?.filter(
    file => !file.tag?.includes('video')
  )

  const cover =
    listing.cover_photo?.aws_s3_key || listing.cover_photo_aws_s3_key
  const images = !isEmptyOrNull(unitImageFiles)
    ? unitImageFiles
    : [{ aws_s3_key: cover }]

  const getBlurredCover = images => {
    if (!images.length) return false
    const cover = images.find(({ tag }) => tag === 'cover_photo')
    return `${cover?.aws_s3_key}` ?? `${images[0].aws_s3_key}`
  }
  const src = getBlurredCover(images)
  const blurredSrc = blurImage(src, {
    width: 300,
    filterType: 'blur',
    quality: 30
  })

  const listingLink = getUrl(listing)

  const { visible, showModal, closeModal } = useDialogController()
  const ensureLoggedIn = useEnsureLoggedIn({ showLogin: showModal })

  return (
    <Container>
      <Wrapper
        height={size.image}
        rel="noopener noreferrer"
        target={newTab ? '_blank' : undefined}
        isRow={isRow}
      >
        <Box style={{ height: '150px' }}>
          <Image
            blurDataURL={blurredSrc}
            src={blurredSrc}
            alt={'test'}
            sizes="345px"
            layout="fill"
            objectFit="cover"
          />
        </Box>

        {skeleton ? (
          <Skeleton.Shape width="100%" height="100%" />
        ) : (
          <Box
            zIndex="modal"
            position="absolute"
            width="full"
            style={{
              top: '80px',
              left: '50%',
              transform: 'translateX(-50%)'
            }}
          >
            <Inline align="center" space="none">
              {!agreedToTerms &&
                (isLoggedIn ? (
                  <DSButton
                    icon={<IconArrowUpRight />}
                    variant="ghost"
                    size="small"
                    id="listing_taptoview_map_click"
                    onClick={e => {
                      e.preventDefault()
                      e.stopPropagation()

                      setListingCardDataLayer({
                        listing,
                        featured: false,
                        eventId: 'listing_taptoview_map_click'
                      })
                      showModalForRequiredTerms('trreb_agreements', () =>
                        window.open(listingLink, '_blank')
                      )
                    }}
                  >
                    {t('b.auth.view.tap_to_view', 'Tap to view')}
                  </DSButton>
                ) : (
                  <DSButton
                    icon={<IconLockOutline />}
                    variant="ghost"
                    size="small"
                    id="listing_logintoview_map_click"
                    onClick={e => {
                      e.preventDefault()
                      e.stopPropagation()

                      setListingCardDataLayer({
                        listing,
                        featured: false,
                        eventId: 'listing_logintoview_map_click'
                      })
                      ensureLoggedIn(() =>
                        showModalForRequiredTerms('trreb_agreements', () =>
                          window.open(listingLink, '_blank')
                        )
                      )
                    }}
                  >
                    {t('b.auth.view.login_to_view', 'Log in to View')}
                  </DSButton>
                ))}
            </Inline>
          </Box>
        )}
        <PillWrapper>{<VerifiedPill />}</PillWrapper>

        <Box padding="xxsmall" width="full" background="white" zIndex="modal">
          <PriceRow>
            <div>
              <Amount id="listing-card-price" priceStyles={priceStyles}>
                <BlurredText>$****</BlurredText>
              </Amount>
              <BlurredText variant={{ size: 'xsmall', weight: 'medium' }}>
                {' '}
                / ****
              </BlurredText>
            </div>
          </PriceRow>
          <DetailRow>
            <BlurredText variant={{ size: 'xsmall', weight: 'medium' }}>
              ****
            </BlurredText>
            <BlurredText variant={{ size: 'xsmall', weight: 'medium' }}>
              ·
            </BlurredText>
            <BlurredText variant={{ size: 'xsmall', weight: 'medium' }}>
              * Bed
            </BlurredText>
            <BlurredText variant={{ size: 'xsmall', weight: 'medium' }}>
              ·
            </BlurredText>
            <BlurredText variant={{ size: 'xsmall', weight: 'medium' }}>
              * Bath
            </BlurredText>
          </DetailRow>
          <Box marginTop="small">
            <ListingCardActions
              listing={listing}
              messageClickId="message-map-click"
              isCardLocked
              stackButtons
            />
          </Box>
        </Box>
      </Wrapper>
      <DSLoginModal
        mode={'login'}
        eventId="web-signup-submit-login-redirect-mls"
        visible={visible}
        onClose={closeModal}
        t={t}
      />
    </Container>
  )
}

export default SingleUnitBlurred
